import { Button, Icon, Input, Select, SelectOption, Text } from "@showitinc/showit-wc";
import cx from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "../../base/Dialog";
import LoadingSpinner from "../../LoadingSpinner";
import useStoreState from "../../providers/useStoreState";
import usePexelsPhotos from "../../../hooks/query/usePexelsPhotos";
import { getPexelsErrorMessage, PEXELS_ORIENTATIONS } from "../../../service/PexelsService";
import SiteDesignStore from "../../../stores/SiteDesignStore";
import { getRecentPexelsPhotos } from "../../../utils/PexelsRecentsUtils";
import { getPexelsCredit } from "../../../utils/PexelsUtils";
import uiPrefix from "../../../utils/UiPrefixUtils";

import styles from "./StockPhotoSearch.module.css";

const SEARCH_DEBOUNCE_MS = 350;
const LOAD_MORE_THRESHOLD_PX = 320;

const PEXELS_HOME_URL = "https://www.pexels.com";

const ORIENTATION_OPTIONS = [
  { value: PEXELS_ORIENTATIONS.ALL, label: "Any orientation" },
  { value: PEXELS_ORIENTATIONS.LANDSCAPE, label: "Landscape" },
  { value: PEXELS_ORIENTATIONS.PORTRAIT, label: "Portrait" },
  { value: PEXELS_ORIENTATIONS.SQUARE, label: "Square" },
];

const readEventValue = (e) => e?.detail?.value ?? e?.target?.value ?? "";

/**
 * @param {Object} props
 * @param {import("@/types/data/Pexels.mjs").PexelsPhoto} props.photo
 * @param {boolean} props.selected
 * @param {(photo) => void} props.onSelect
 * @param {(photo) => void} props.onConfirm
 * @param {string} [props.className]
 * @param {string} [props.testId]
 */
const PhotoTile = ({
  photo,
  selected,
  onSelect,
  onConfirm,
  className,
  testId = "pexels-photo-tile",
}) => (
  <button
    type="button"
    className={cx(styles.tile, className, { [styles.selected]: selected })}
    style={{ backgroundColor: photo.avgColor || undefined }}
    onClick={() => onSelect(photo)}
    onDoubleClick={() => onConfirm(photo)}
    aria-pressed={selected}
    aria-label={photo.alt || getPexelsCredit(photo)}
    data-testid={testId}
    data-photo-id={photo.id}
  >
    <img className={styles.thumb} src={photo.thumbnail} alt="" loading="lazy" draggable={false} />
    <span className={styles.credit}>{photo.photographer}</span>
    {selected && (
      <span className={styles.check}>
        <Icon type="check" size="16" />
      </span>
    )}
  </button>
);

/**
 * Pexels picker shown as the Stock Photos tab of the media library. Renders the
 * dialog body and footer itself, so it must sit directly inside a Modal.Dialog.
 * @param {Object} props
 * @param {() => void} props.onCancel
 * @param {(photo: import("@/types/data/Pexels.mjs").PexelsPhoto) => void} props.onSelect
 * @param {string} [props.confirmLabel="Use Photo"]
 * @param {string} [props.confirmVariant="feature"]
 * @param {(ctx: {selectedPhoto: Object|null, confirmSelected: () => void}) => React.ReactNode} [props.aside]
 *   renders beside the results, e.g. the gallery list
 * @param {React.ReactNode} [props.extraActions] footer buttons after the confirm button
 */
const StockPhotoSearch = ({
  onCancel,
  onSelect,
  confirmLabel = "Use Photo",
  confirmVariant = "feature",
  aside,
  extraActions = null,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [query, setQuery] = useState("");
  const [orientation, setOrientation] = useState(PEXELS_ORIENTATIONS.ALL);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [pageError, setPageError] = useState(null);
  const gridRef = useRef(null);
  const searchInputRef = useRef(null);
  const committedQueryRef = useRef("");

  // Subscribe to the Immutable list so unrelated design changes keep the reference stable.
  const recentsList = useStoreState(
    SiteDesignStore,
    () => SiteDesignStore.getDesign()?.recentPexelsPhotos,
  );
  const recentPhotos = useMemo(() => getRecentPexelsPhotos(recentsList), [recentsList]);

  const startNewResults = useCallback(() => {
    setSelectedPhoto(null);
    setPageError(null);
    if (gridRef.current) gridRef.current.scrollTop = 0;
  }, []);

  // The debounce timer still fires once on open and after Enter, so clearing
  // unconditionally would wipe a pick made inside that window.
  const applyQuery = useCallback(
    (nextQuery) => {
      if (committedQueryRef.current === nextQuery) return;
      committedQueryRef.current = nextQuery;

      setQuery(nextQuery);
      startNewResults();

      // Orientation never reaches the endpoint without a query.
      if (!nextQuery.trim()) setOrientation(PEXELS_ORIENTATIONS.ALL);
    },
    [startNewResults],
  );

  const applyOrientation = (nextOrientation) => {
    setOrientation(nextOrientation);
    startNewResults();
  };

  // `autofocus` only fires when the modal opens, not when this tab mounts inside
  // an open one; the frame lets the modal finish its own focus handling first.
  useEffect(() => {
    const frame = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => applyQuery(inputValue), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, applyQuery]);

  const {
    photos,
    totalResults,
    isPending,
    isError,
    error,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = usePexelsPhotos({ query, orientation });

  // v5 exposes no flag for a failed page; the rejection is the only signal.
  const loadNextPage = () => {
    setPageError(null);
    fetchNextPage({ throwOnError: true }).catch(setPageError);
  };

  // Cleared so a picker that stays open (gallery mode) is ready for the next pick.
  const commitPhoto = (photo) => {
    if (!photo || !onSelect) return;
    onSelect(photo);
    setSelectedPhoto(null);
  };

  // Stable so a memoized aside doesn't re-render on every keystroke.
  const confirmSelectedRef = useRef(null);
  confirmSelectedRef.current = () => commitPhoto(selectedPhoto);
  const confirmSelected = useCallback(() => confirmSelectedRef.current(), []);

  const onGridScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    const nearBottom = scrollTop + clientHeight >= scrollHeight - LOAD_MORE_THRESHOLD_PX;
    if (nearBottom && hasNextPage && !isFetchingNextPage) {
      loadNextPage();
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      applyQuery(inputValue);
    }
  };

  const isSearching = query.trim().length > 0;
  const showInitialLoading =
    isPending || (isFetching && !isFetchingNextPage && photos.length === 0);

  const renderResults = () => {
    // A failed "load more" also sets isError; only an empty grid is fatal.
    if (isError && photos.length === 0) {
      return (
        <div className={styles.state} data-testid="pexels-error">
          <Text>{getPexelsErrorMessage(error)}</Text>
          <Button variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      );
    }

    if (showInitialLoading) {
      return (
        <div className={styles.state} data-testid="pexels-loading">
          <LoadingSpinner />
        </div>
      );
    }

    if (photos.length === 0) {
      return (
        <div className={styles.state} data-testid="pexels-empty">
          <Text>No photos found{isSearching ? ` for “${query.trim()}”` : ""}.</Text>
          <Text className={styles["state-detail"]}>Try a different search or orientation.</Text>
        </div>
      );
    }

    return (
      <>
        <div className={styles.grid} data-testid="pexels-grid">
          {photos.map((photo) => (
            <PhotoTile
              key={photo.id}
              photo={photo}
              selected={selectedPhoto?.id === photo.id}
              onSelect={setSelectedPhoto}
              onConfirm={commitPhoto}
            />
          ))}
        </div>
        <div className={styles["load-more"]}>
          {isFetchingNextPage && <LoadingSpinner />}
          {(pageError || isError) && (
            <Text className={styles["state-detail"]} data-testid="pexels-page-error">
              {getPexelsErrorMessage(pageError || error)}
            </Text>
          )}
          {!isFetchingNextPage && hasNextPage && (
            <Button
              variant="outline"
              onClick={loadNextPage}
              data-e2e={uiPrefix("pexels-load-more-btn")}
            >
              Load more
            </Button>
          )}
        </div>
      </>
    );
  };

  const footer = (
    <div className={styles.footer}>
      <div className={styles["footer-left"]}>
        <Text className={styles.attribution}>
          Photos provided by{" "}
          <a href={PEXELS_HOME_URL} target="_blank" rel="noopener noreferrer">
            Pexels
          </a>
        </Text>
        {selectedPhoto && (
          <Text className={styles["selected-credit"]} data-testid="pexels-selected-credit">
            {selectedPhoto.photographerUrl ? (
              <a href={selectedPhoto.photographerUrl} target="_blank" rel="noopener noreferrer">
                {getPexelsCredit(selectedPhoto)}
              </a>
            ) : (
              getPexelsCredit(selectedPhoto)
            )}
          </Text>
        )}
      </div>
      <div className={styles["footer-right"]}>
        <Button variant="minimal" onClick={onCancel} data-e2e={uiPrefix("pexels-cancel-btn")}>
          Cancel
        </Button>
        <Button
          variant={confirmVariant}
          disabled={!selectedPhoto}
          onClick={() => commitPhoto(selectedPhoto)}
          data-e2e={uiPrefix("pexels-use-photo-btn")}
          data-testid="pexels-use-photo"
        >
          {confirmLabel}
        </Button>
        {extraActions}
      </div>
    </div>
  );

  return (
    <>
      <Dialog.Body style={{ padding: 0, display: "flex" }}>
        <div className={styles.body} data-testid="stock-photo-search">
          <div className={styles.controls}>
            <Input
              ref={searchInputRef}
              className={styles.search}
              type="search"
              fullWidth
              placeholder="Search free stock photos"
              value={inputValue}
              onSIInput={(e) => setInputValue(readEventValue(e))}
              onSIChange={(e) => setInputValue(readEventValue(e))}
              onKeyDown={onSearchKeyDown}
              data-testid="pexels-search-input"
            />
            <Select
              className={styles.orientation}
              hideLabel
              disabled={!isSearching}
              value={orientation}
              onSISelect={(e) => applyOrientation(readEventValue(e))}
              data-testid="pexels-orientation"
            >
              {ORIENTATION_OPTIONS.map(({ value, label }) => (
                <SelectOption key={value || "all"} value={value}>
                  {label}
                </SelectOption>
              ))}
            </Select>
          </div>
          {!isError && !showInitialLoading && photos.length > 0 && (
            <Text className={styles.summary} data-testid="pexels-summary">
              {isSearching
                ? `${totalResults.toLocaleString()} results for “${query.trim()}”`
                : "Curated by Pexels"}
            </Text>
          )}
          <div ref={gridRef} className={styles.results} onScroll={onGridScroll}>
            {renderResults()}
          </div>
          {recentPhotos.length > 0 && (
            <div className={styles.recents} data-testid="pexels-recents">
              <Text className={styles["recents-label"]}>Recently used</Text>
              <div className={styles["recents-strip"]}>
                {recentPhotos.map((photo) => (
                  <PhotoTile
                    key={`recent-${photo.id}`}
                    className={styles["recent-tile"]}
                    photo={photo}
                    selected={selectedPhoto?.id === photo.id}
                    onSelect={setSelectedPhoto}
                    onConfirm={commitPhoto}
                    testId="pexels-recent-tile"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        {aside?.({ selectedPhoto, confirmSelected })}
      </Dialog.Body>
      <Dialog.Footer>{footer}</Dialog.Footer>
    </>
  );
};

export default StockPhotoSearch;
